import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion"; // For animations
import { FaLinkedinIn, FaGithub, FaLightbulb } from "react-icons/fa"; // Updated icons to include social media
import { FaXTwitter } from "react-icons/fa6";
import { Link } from "react-router-dom"; // For navigation links
import profilePic from "../assets/images/Tirthoraj-profile-pic-4.png"; // Real photo
import ghibliPic from "../assets/images/Tirtho-ghibli-2.png"; // Ghibli style image

// Array of inspirational tech quotes
const techQuotes = [
  {
    text: "The people who are crazy enough to think they can change the world are the ones who really do!",
    author: "Steve Jobs",
    color: "red-500",
  },
  {
    text: "Innovation distinguishes between a leader and a follower.",
    author: "Steve Jobs",
    color: "blue-500",
  },
  {
    text: "The only way to do great work is to love what you do.",
    author: "Steve Jobs",
    color: "purple-500",
  },
  {
    text: "Code is like humor. When you have to explain it, it's bad.",
    author: "Cory House",
    color: "emerald-500",
  },
  {
    text: "First, solve the problem. Then, write the code.",
    author: "John Johnson",
    color: "orange-500",
  },
  {
    text: "Experience is the name everyone gives to their mistakes.",
    author: "Oscar Wilde",
    color: "pink-500",
  },
  {
    text: "In order to be irreplaceable, one must always be different.",
    author: "Coco Chanel",
    color: "indigo-500",
  },
  {
    text: "Java is to JavaScript what car is to Carpet.",
    author: "Chris Heilmann",
    color: "yellow-500",
  },
  {
    text: "The best way to get started is to quit talking and begin doing.",
    author: "Walt Disney",
    color: "green-500",
  },
  {
    text: "The computer was born to solve problems that did not exist before.",
    author: "Bill Gates",
    color: "cyan-500",
  },
  {
    text: "Software is a great combination between artistry and engineering.",
    author: "Bill Gates",
    color: "violet-500",
  },
  {
    text: "Any fool can write code that a computer can understand. Good programmers write code that humans can understand.",
    author: "Martin Fowler",
    color: "rose-500",
  },
  {
    text: "Simplicity is the ultimate sophistication.",
    author: "Leonardo da Vinci",
    color: "amber-500",
  },
  {
    text: "The function of good software is to make the complex appear to be simple.",
    author: "Grady Booch",
    color: "teal-500",
  },
  {
    text: "Programs must be written for people to read, and only incidentally for machines to execute.",
    author: "Harold Abelson",
    color: "slate-500",
  },
];

function About() {
  const aboutRef = useRef(null);
  const [currentQuote, setCurrentQuote] = useState(techQuotes[0]);

  useEffect(() => {
    const currentRef = aboutRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    // Set random quote on component mount
    const randomQuote =
      techQuotes[Math.floor(Math.random() * techQuotes.length)];
    setCurrentQuote(randomQuote);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  // Framer Motion variants for animations
  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const quoteVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="about"
      className="min-h-screen bg-gray-900 py-32 px-6 sm:px-10 lg:px-12 relative overflow-hidden"
      ref={aboutRef}
    >
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;800&family=JetBrains+Mono:wght@400;700&display=swap');
          .link-hover {
            position: relative;
            display: inline-block;
            color: #10B981; /* Emerald green for links */
            text-decoration: none;
            transition: color 0.3s ease, transform 0.3s ease;
          }
          .link-hover::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -4px;
            left: 0;
            background: #10B981;
            transition: width 0.3s ease;
          }
          .link-hover:hover {
            color: #059669; /* Darker emerald green on hover */
            transform: translateY(-2px);
          }
          .link-hover:hover::after {
            width: 100%;
          }
          .animate-fade-in {
            animation: fadeIn 1s ease-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          /* Enhanced image styles for better quality */
          .profile-image {
            image-rendering: -webkit-optimize-contrast;
            image-rendering: optimize-quality;
            image-rendering: crisp-edges;
            image-rendering: auto;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            transform: translateZ(0);
            -webkit-transform: translateZ(0);
          }
          
          /* 3D Flip Card Effect */
          .flip-card {
            background-color: transparent;
            perspective: 1000px;
            width: 320px;
            height: 320px;
          }
          
          .flip-card-inner {
            position: relative;
            width: 100%;
            height: 100%;
            text-align: center;
            transition: transform 0.8s;
            transform-style: preserve-3d;
          }
          
          .flip-card:hover .flip-card-inner {
            transform: rotateY(180deg);
          }
          
          .flip-card-front, .flip-card-back {
            position: absolute;
            width: 100%;
            height: 100%;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            border-radius: 12px;
            overflow: hidden;
          }
          
          .flip-card-back {
            transform: rotateY(180deg);
          }
          
          .flip-card-front img, .flip-card-back img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center top;
            image-rendering: -webkit-optimize-contrast;
            image-rendering: optimize-quality;
            image-rendering: high-quality;
            image-rendering: auto;
            filter: contrast(1.02) saturate(1.1);
          }
          
          /* Specific adjustments for Ghibli image to prevent top cropping */
          .flip-card-front img {
            object-position: center 15%;
            transform: scale(1.05);
          }
          
          /* Real photo optimization */
          .flip-card-back img {
            object-position: center 20%;
            image-rendering: -webkit-optimize-contrast;
            image-rendering: optimize-quality;
            image-rendering: auto;
            filter: contrast(1.05) saturate(1.08) brightness(1.02);
          }
          
          /* Responsive sizing */
          @media (max-width: 640px) {
            .flip-card {
              width: 288px;
              height: 288px;
            }
          }
        `}
      </style>

      <div className="relative z-10 flex flex-col md:flex-row items-start space-y-12 md:space-y-0 md:space-x-12">
        {/* Left Section: Center-Aligned Title, Image & Tagline */}
        <div className="w-full md:w-2/5 flex flex-col items-center text-center md:text-left">
          {/* Title (Center Aligned) */}
          <motion.h2
            className="text-4xl md:text-6xl font-bold font-['Inter'] mb-8 tracking-tight text-emerald-400"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={{
              hidden: { opacity: 0, y: -20 },
              visible: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            My Story
          </motion.h2>

          {/* Profile Image with 3D Flip Effect */}
          <div className="relative group pt-10">
            <motion.div
              className="flip-card mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1 },
              }}
              transition={{ duration: 0.3 }}
            >
              <div className="flip-card-inner">
                {/* Front - Ghibli Image */}
                <div className="flip-card-front">
                  <img
                    src={ghibliPic}
                    alt="Tirthoraj Bhattacharya - Ghibli Style"
                    className="profile-image border-4 border-gray-700/40 shadow-lg transition duration-300 ease-in-out group-hover:border-emerald-400/40"
                    loading="eager"
                    decoding="sync"
                  />
                  {/* Front badge */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-purple-600/90 text-white text-xs px-3 py-1.5 rounded-full font-medium backdrop-blur-sm border border-purple-400/30">
                    Hover to Real Me!
                  </div>
                </div>

                {/* Back - Real Image */}
                <div className="flip-card-back">
                  <img
                    src={profilePic}
                    alt="Tirthoraj Bhattacharya"
                    className="profile-image border-4 border-emerald-400/60 shadow-lg"
                    loading="eager"
                    decoding="sync"
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/320x320?text=No+Image";
                    }}
                  />
                  {/* Back badge
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-emerald-600/90 text-white text-xs px-3 py-1.5 rounded-full font-medium backdrop-blur-sm border border-emerald-400/30">
                    📸 The Real Me!
                  </div> */}
                </div>
              </div>

              {/* Glowing effect on hover */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-30 transition-all duration-500 bg-gradient-to-r from-purple-400/20 via-emerald-400/20 to-blue-400/20 blur-lg -z-10"></div>
            </motion.div>
          </div>

          {/* Tagline - Enhanced with modern styling */}
          <motion.div
            className="text-center mt-8 pt-4 px-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={textVariants}
            transition={{ delay: 0.4 }}
          >
            <p className="text-white text-lg md:text-xl font-medium font-['Inter'] tracking-normal inline-flex flex-wrap justify-center items-center gap-2">
              <span className="text-gray-300">A journey of</span>

              <Link to="/education" className="relative group">
                <span className="relative z-10 text-gray-900 font-bold px-3 py-1">
                  learning
                </span>
                <span className="absolute inset-0 bg-emerald-400 rounded transform transition-all duration-300 group-hover:scale-110 group-hover:bg-emerald-300"></span>
              </Link>

              <span className="text-gray-300">,</span>

              <Link to="/experience" className="relative group">
                <span className="relative z-10 text-gray-900 font-bold px-3 py-1">
                  growth
                </span>
                <span className="absolute inset-0 bg-emerald-400 rounded transform transition-all duration-300 group-hover:scale-110 group-hover:bg-emerald-300"></span>
              </Link>

              <span className="text-gray-300">, and</span>

              <Link to="/projects" className="relative group">
                <span className="relative z-10 text-gray-900 font-bold px-3 py-1">
                  code
                </span>
                <span className="absolute inset-0 bg-emerald-400 rounded transform transition-all duration-300 group-hover:scale-110 group-hover:bg-emerald-300"></span>
              </Link>

              <span className="text-gray-300">.</span>
            </p>
          </motion.div>
        </div>

        {/* Right Section: Full-Width Text & Quote */}
        <div className="w-full space-y-8 text-gray-100/90 text-lg leading-relaxed font-normal font-['Inter'] cursor-default">
          <motion.p
            className="p-3 rounded-lg"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 0.2 }}
          >
            It all started with a simple obsession:{" "}
            <span className="font-bold text-emerald-400">
              "How do computers even work?"
            </span>{" "}
            As a kid, I was captivated by screens—flashing pixels, animated
            menus, and the magic behind the click of a button. Then came my
            cousin brother, the gateway to my next addiction:{" "}
            <span className="font-bold text-emerald-400">PC Gaming</span>. It
            wasn't just about playing—it was about wondering how those virtual
            worlds were built. And that curiosity led me to my very first line
            of code:{" "}
            <span className="font-bold text-emerald-400">"Hello, World!"</span>{" "}
            Simple. Classic. Mind-blowing.
          </motion.p>

          <motion.p
            className="p-3 rounded-lg"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 0.4 }}
          >
            Writing code felt like unlocking a superpower. At first, it was all
            about
            <span className="font-bold text-emerald-400">
              {" "}
              designing websites
            </span>
            — experimenting with layouts, crafting UI elements, and making
            things look good. But then came the real thrill—solving problems.
            Not just{" "}
            <span className="font-bold text-emerald-400">LeetCode</span> puzzles
            or algorithm challenges, but actual{" "}
            <span className="font-bold text-emerald-400">
              real-world problems
            </span>
            . The kind where code isn't just code—it's impact.
          </motion.p>

          <motion.p
            className="p-3 rounded-lg"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 0.6 }}
          >
            That's when the mission changed:{" "}
            <span className="font-bold text-emerald-400">
              Build apps that truly matter
            </span>
            . Not just projects for the sake of a portfolio, but solutions that
            improve lives, automate workflows, and
            <span className="font-bold text-emerald-400">
              {" "}
              create an actual difference
            </span>
            . My job in IT gave me a reality check—technology isn't just about
            elegant code; it's about{" "}
            <span className="font-bold text-emerald-400">
              impacting real people
            </span>
            . I saw firsthand how software shapes businesses, streamlines
            industries, and fuels innovation. But I wanted more. I wanted to
            build from the ground up.
          </motion.p>

          <motion.p
            className="p-3 rounded-lg"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 0.8 }}
          >
            So I made the leap. I left my job and dived into{" "}
            <span className="font-bold text-emerald-400">
              M.Tech in Software Engineering
            </span>
            . Not for the degree, but for the knowledge, the exposure, and the
            tools to{" "}
            <span className="font-bold text-emerald-400">
              build meaningful software{" "}
            </span>
            at scale. AI is the future, no doubt—but rather than just building
            models, I want to integrate AI into full-fledged software systems .
            That's the vision:{" "}
            <span className="font-bold text-emerald-400">
              Engineering Solutions, not just Training Models.
            </span>
          </motion.p>

          <motion.p
            className="p-3 rounded-lg"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 1.0 }}
          >
            There's still{" "}
            <span className="font-bold text-emerald-400">so much to learn</span>
            , so much to build, and so many problems to solve. But one thing's
            for sure:{" "}
            <span className="font-bold text-emerald-400">
              THIS JOURNEY IS JUST THE BEGINNING!
            </span>{" "}
            And I can't wait to see where it leads.
          </motion.p>

          {/* Random Tech Quote - Fixed container with stable layout */}
          <motion.blockquote
            className={`cursor-default border-l-4 border-${currentQuote.color} pl-4 text-indigo-300 bg-gray-800/20 p-4 rounded-lg shadow-md relative group hover:bg-gray-700/20 transition-all duration-300 min-h-[140px] flex flex-col justify-center`}
            variants={quoteVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.6 }}
          >
            <div
              className={`absolute -left-2 top-1/2 transform -translate-y-1/2 w-1 h-16 bg-${currentQuote.color} rounded-r-full opacity-0 group-hover:opacity-50 transition-opacity duration-300`}
            ></div>

            <div className="flex items-start gap-3">
              <FaLightbulb className="text-yellow-500 text-2xl mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-['JetBrains_Mono'] font-bold text-base md:text-lg tracking-normal text-left hover:text-emerald-400 leading-relaxed">
                  "{currentQuote.text}"
                </p>
                <p className={`text-right mt-3 font-semibold text-red-600`}>
                  - {currentQuote.author}
                </p>
              </div>
            </div>
          </motion.blockquote>

          {/* Social Media Links */}
          <motion.div
            className="mt-6 flex justify-center md:justify-end gap-4"
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ delay: 1.4 }}
          >
            <a
              href="https://www.linkedin.com/in/tirthoraj-bhattacharya/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-400 transition transform hover:scale-105"
            >
              <FaLinkedinIn size={24} />
            </a>
            <a
              href="https://github.com/CodeTirtho97"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-400 transition transform hover:scale-105"
            >
              <FaGithub size={24} />
            </a>
            <a
              href="https://x.com/lucifer_7951"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-400 transition transform hover:scale-105"
            >
              <FaXTwitter size={24} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default About;
